// Screen Breakpoints
import { breakpointsVuetifyV3, useBreakpoints } from '@vueuse/core'
const breakpoints = useBreakpoints(breakpointsVuetifyV3)
const { width, height } = useWindowSize()

const navigationItems = {
    home: {
      title: 'Home',
      route: { name: 'home' }
    },
    insights: {
      title: 'Insights',
      route: { name: 'insights-index' }
    },
    investors: {
      title: 'Investors',
      route: { name: 'investors-index' },
    },
    investorsScout: {
      title: 'Scout',
      route: { name: 'scout' },
      flag: { text: 'New', color: 'green-accent-2'}
    },
    startups: {
      title: 'Startups',
      route: { name: 'startups-index' }
    },
    people: {
      title: 'People',
      route: { name: 'people-index' }
    },
    chatbots: {
      title: 'Chatbots',
      route: { name: 'chatbots-index' }
    },
    news: {
      title: 'News',
      route: { name: 'news-index' }
    },
    onboarding: {
      title: 'Onboarding',
      route: { name: 'onboarding-index' },
      flag: { text: 'New', color: 'green-accent-2'}
    },
    programs: {
      title: 'Programs',
      route: { name: 'programs-index' }
    },
    forms: {
      title: 'Forms',
      route: { name: 'form-tokens' }
    },
    logs: {
      title: 'Logs',
      route: { name: 'service-logs' }
    },
    sosvUniversity: {
      title: 'SOSV University',
      href: 'https://sites.google.com/sosv.com/sosvuniversity/'
    },
    sosvCentral: {
      title: 'SOSV Central',
      href: 'https://sosv.login.duosecurity.com/central/'
    },
    wiki: {
      title: 'Wiki',
      route: { name: 'wiki-home', params: { wiki: 'sosv' } },
      flag: { text: 'New', color: 'green-accent-2'}
    }
  }

export const useUiStore = defineStore('ui', {
  state: () => {
    return {
      maxWidth: 1400,
      universalSearch: false,
      showSync: false,
      beta: false,
      compactView: false,
      allBreadcrumbs: [] as (BreadcrumbItem | null)[],
      navigator: useDialog('quick-search'),
      pageActions: [] as MenuItem[],
      pageFavorite: undefined as CollectionItem | undefined,
      navigation: useDialog('navigation-dialog', true),
      filters: useDialog('mobile-filters'),
      confirmation: useDialog('confirmation-dialog'),
      instructions: useDialog('instructions-dialog'),
      // TODO: Package this into a single object
      confirmationMessage: '',
      confirmationContinue: '',
      confirmationCancel: '',
      confirmationAction: undefined as (() => void) | undefined,
      algoliaSearches: {} as Record<string, AlgoliaSearch>,
      lists: {} as Record<string, any[]>
    }
  },
  actions: {
    async fetchLists() {
      if (Object.keys(this.lists).length > 0) return
      const fetch = useMoriaFetch()
      const { data } = await fetch<Record<string, any[]>>('/lists').json()
      if (data.value) this.lists = data.value
    },
    toggleUniversalSearch() {
      this.universalSearch = !this.universalSearch
    },
    setAlgoliaSearch(index: string, search: AlgoliaSearch) {
      this.algoliaSearches[index] = search
    },
    getAlgoliaSearch(index: string, defaultSearch: { query?: string, filters?: Record<string, string[]> }): AlgoliaSearch {
      return this.algoliaSearches[index] ?? defaultSearch ?? {}
    },
    setBeta() {
      this.beta = true
    },
    addBreadcrumb(level: number, item: BreadcrumbItem) {
      this.allBreadcrumbs[level - 1] = item
    },
    clearNavigation() {
      this.navigation.close()
    },
    clearBreadcrumb(level: number) {
      this.allBreadcrumbs[level - 1] = null
    },
    clearBreadcrumbs(to: any) {
      const router = useRouter()
      if (to) {
        // Clear breadcrumbs that are not part of the destination URL
        const destination = to.fullPath
        this.allBreadcrumbs.map((breadcrumb, index) => {
          // Calculate route for this breadcrumb
          const breadcrumbRoute = breadcrumb?.to ? router.resolve(breadcrumb?.to).fullPath : false //useRoute().fullPath
          // Remove breadcrumb if it doesn't have a route or is part of the destination URL
          if (!breadcrumbRoute || !destination.startsWith(breadcrumbRoute)) {
            this.allBreadcrumbs[index] = null
          }
        })
      } else {
        // Clear all breadcrumbs
        this.allBreadcrumbs = []
      }
      // Clear beta flag
      this.beta = false
    },
    clearPageFavorite() {
      this.pageFavorite = undefined
    },
    setPageFavorite(favorite: CollectionItem) {
      this.pageFavorite = favorite
    },
    clearPageActions() {
      this.pageActions = []
    },
    setPageActions(actions: MenuItem[] | Ref<MenuItem[]>) {
      this.pageActions = unref(actions)
    },
    cancelConfirmationAction() {
      this.confirmation.close()
    },
    executeConfirmationAction() {
      if (this.confirmationAction) {
        this.confirmationAction()
        this.confirmationAction = undefined
      }
      this.confirmation.close()
    },
    showConfirmation(dialog: ConfirmationDialog) {
      this.confirmationMessage = dialog.message
      this.confirmationContinue = dialog.confirmText || 'OK'
      this.confirmationCancel = dialog.cancelText || 'Cancel'
      this.confirmationAction = dialog.action
      this.confirmation.open()
    },
  },
  getters: {
    getList: (state) => (name: string): any[] => state.lists[name] ?? [],
    xsmall: () => width.value < 390,
    mobile: () => breakpoints.smaller('sm').value,
    desktop(): boolean { return !this.mobile },
    width: () => width.value,
    height: () => height.value,
    breadcrumbs: (state):Breadcrumbs =>
    // Remove empty breadcrumbs
    state.allBreadcrumbs.filter((breadcrumb) => breadcrumb),
    navigationItems: () => navigationItems,
    navigationItemsList: () => Object.values(navigationItems)
      .filter((item: any) => !!item.route?.name)
      .map((item: any) => ({ title: item.title, value: item.route.name }))
      .sort((a, b) => a.title.localeCompare(b.title))
  }
})
